import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  Clock3,
  ExternalLink,
  FlaskConical,
  Search,
} from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { CopyButton } from '../components/CopyButton';
import {
  GUIDE_CATEGORIES,
  guideArticleById,
  searchGuideArticles,
  type GuideArticle,
  type GuideSection,
} from '../guide-content';

function articleIdFromLocation() {
  if (typeof window === 'undefined') return 'start-here';
  return window.location.pathname.match(/\/guide\/(?<articleId>[a-z0-9-]+)\/?$/i)?.groups?.['articleId'] || 'start-here';
}

function GuideStatusMark({ article, compact = false }: { article: GuideArticle; compact?: boolean }) {
  const isAvailable = article.status === 'available';
  return (
    <span className={`guideStatus guideStatus--${article.status}${compact ? ' guideStatus--compact' : ''}`}>
      {isAvailable ? <CheckCircle2 size={13} /> : <FlaskConical size={13} />}
      {compact ? (isAvailable ? 'Live' : 'Planned') : (isAvailable ? 'Available now' : 'Implementation blueprint')}
    </span>
  );
}

function GuideSectionContent({ section }: { section: GuideSection }) {
  return (
    <section className="guideSection" id={`guide-section-${section.id}`}>
      <h2>{section.title}</h2>
      {section.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
      {section.bullets ? (
        <ul className="guideBulletList">
          {section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
        </ul>
      ) : null}
      {section.steps ? (
        <ol className="guideSteps">
          {section.steps.map((step, index) => (
            <li key={step.title}>
              <span className="guideStepNumber">{index + 1}</span>
              <div><strong>{step.title}</strong><p>{step.detail}</p></div>
            </li>
          ))}
        </ol>
      ) : null}
      {section.code ? (
        <div className="guideCode">
          <div className="guideCodeHeader"><span>{section.code.label}</span><CopyButton value={section.code.value} label={`Copy ${section.code.label}`} /></div>
          <pre><code>{section.code.value}</code></pre>
        </div>
      ) : null}
      {section.table ? (
        <div className="guideTableWrap">
          <table className="guideTable">
            <thead><tr>{section.table.headers.map((header) => <th key={header}>{header}</th>)}</tr></thead>
            <tbody>{section.table.rows.map((row) => <tr key={row.join('|')}>{row.map((cell, index) => <td key={`${index}-${cell}`}>{cell}</td>)}</tr>)}</tbody>
          </table>
        </div>
      ) : null}
      {section.links ? (
        <div className="guideSourceLinks">
          {section.links.map((link) => (
            <a key={link.href} href={link.href} target="_blank" rel="noreferrer">
              <span><strong>{link.label}</strong>{link.detail ? <small>{link.detail}</small> : null}</span>
              <ExternalLink size={14} aria-hidden="true" />
            </a>
          ))}
        </div>
      ) : null}
      {section.note ? (
        <aside className={`guideNote guideNote--${section.note.tone}`}>
          <strong>{section.note.title}</strong><p>{section.note.body}</p>
        </aside>
      ) : null}
    </section>
  );
}

export function GuidePage() {
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(articleIdFromLocation);
  const selectedArticle = guideArticleById(selectedId);
  const visibleArticles = useMemo(() => searchGuideArticles(query), [query]);

  const selectArticle = useCallback((articleId: string) => {
    setSelectedId(articleId);
    if (typeof window !== 'undefined') {
      const guideRoot = window.location.pathname.replace(/\/guide(?:\/[^/]+)?\/?$/, '/guide');
      window.history.replaceState(
        window.history.state,
        '',
        `${guideRoot}/${articleId}${window.location.search}`
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  return (
    <div className="guideShell">
      <aside className="guideNav" aria-label="Guide navigation">
        <div className="guideSearch">
          <Search size={15} aria-hidden="true" />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search the guide" aria-label="Search the guide" />
        </div>
        {query ? <div className="guideSearchCount">{visibleArticles.length} {visibleArticles.length === 1 ? 'result' : 'results'}</div> : null}
        <div className="guideNavScroll">
          {GUIDE_CATEGORIES.map((category) => {
            const categoryArticles = visibleArticles.filter((article) => article.category === category);
            if (!categoryArticles.length) return null;
            return (
              <div className="guideNavGroup" key={category}>
                <div className="guideNavLabel">{category}</div>
                {categoryArticles.map((article) => (
                  <button key={article.id} type="button" className={`guideNavItem${selectedArticle.id === article.id ? ' is-active' : ''}`} onClick={() => selectArticle(article.id)}>
                    <span>{article.title}</span>
                    <i className={`guideAvailabilityDot guideAvailabilityDot--${article.status}`} aria-label={article.status === 'available' ? 'Available now' : 'Implementation blueprint'} />
                  </button>
                ))}
              </div>
            );
          })}
          {!visibleArticles.length ? <div className="guideEmptySearch">No guide matches “{query}”. Try a system name, workflow step or configuration term.</div> : null}
        </div>
      </aside>

      <article className="guideArticle">
        <header className="guideArticleHeader">
          <div className="guideArticleMeta"><span>{selectedArticle.category}</span><span><Clock3 size={13} />{selectedArticle.minutes} min read</span></div>
          <h1>{selectedArticle.title}</h1>
          <p>{selectedArticle.summary}</p>
          <GuideStatusMark article={selectedArticle} />
        </header>
        <div className="guideArticleBody">
          {selectedArticle.sections.map((section) => <GuideSectionContent key={section.id} section={section} />)}
        </div>
        {selectedArticle.related?.length ? (
          <footer className="guideRelated">
            <div className="guideRelatedLabel">Continue setting up</div>
            {selectedArticle.related.map((articleId) => {
              const article = guideArticleById(articleId);
              return <button type="button" key={article.id} onClick={() => selectArticle(article.id)}><span><small>{article.category}</small>{article.title}</span><ArrowRight size={16} /></button>;
            })}
          </footer>
        ) : null}
      </article>

      <aside className="guideOutline" aria-label="On this page">
        <div className="guideOutlineTitle"><BookOpen size={14} />On this page</div>
        {selectedArticle.sections.map((section) => (
          <button type="button" key={section.id} onClick={() => document.getElementById(`guide-section-${section.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>{section.title}</button>
        ))}
        <div className="guideOutlineLegend">
          <GuideStatusMark article={selectedArticle} compact />
          <p>{selectedArticle.status === 'available' ? 'This workflow is supported in Cindro today.' : 'Use this as the contract for upcoming integration work.'}</p>
        </div>
      </aside>
    </div>
  );
}
